import { useEffect, useState, useRef } from "react";
import { DISPLAY_LANG } from "../../../config";
import { useNavigate } from "react-router-dom";
import { getData } from "country-list";
import axios from "../../../axios";
import "../../../stylesheets/sslGenerateCSR.css";

function CountrySelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const countries = getData()
    .map((c) => ({
      code: c.code,
      name: c.name.replace(/\s*\([^)]*\)/g, "").trim(),
      flagUrl: `https://flagcdn.com/w40/${c.code.toLowerCase()}.png`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCountry = countries.find((c) => c.code === value);

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="country-select-container" ref={containerRef}>
      <div
        className={`country-select-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCountry ? (
          <span className="selected-value">
            <img
              src={selectedCountry.flagUrl}
              alt={selectedCountry.name}
              className="flag-img"
            />
            <span className="name">{selectedCountry.name}</span>
          </span>
        ) : (
          <span className="placeholder">Select Country</span>
        )}
        <span className="arrow">{isOpen ? "▲" : "▼"}</span>
      </div>

      <input
        type="text"
        tabIndex={-1}
        value={value}
        onChange={() => { }}
        required
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          position: "absolute",
          pointerEvents: "none",
        }}
      />

      {isOpen && (
        <div className="country-select-dropdown">
          <input
            type="text"
            className="country-search-input"
            placeholder="Search country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <ul className="country-options-list">
            {filteredCountries.map((c) => (
              <li
                key={c.code}
                className={`country-option ${value === c.code ? "selected" : ""}`}
                onClick={() => {
                  onChange(c.code);
                  setIsOpen(false);
                  setSearch("");
                }}
              >
                <img
                  src={c.flagUrl}
                  alt={c.name}
                  className="flag-img"
                />
                <span className="name">{c.name}</span>
              </li>
            ))}
            {filteredCountries.length === 0 && (
              <li className="no-options">No countries found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const pageText = {
  heading: {
    en: "Generate CSR",
    am: "CSR Ստեղծում",
    ru: "Создать CSR",
  },
  description: {
    en: "Fill the form below to generate a Certificate Signing Request.",
    am: "Լրացրեք ստորև նշված ձևը CSR ստեղծելու համար։",
    ru: "Заполните форму ниже для создания запроса на подпись сертификата.",
  },
  submitButton: {
    en: "Generate CSR",
    am: "Ստեղծել CSR",
    ru: "Создать CSR",
  },
  resultTitle: {
    en: "Generated CSR Result",
    am: "Ստացված CSR",
    ru: "Сформированный CSR",
  },
};

const continiuButtonText = {
  en: "Continue/Loading...",
  am: "Շարունակել/Բեռնվում է...",
  ru: "Продолжить/Загрузка...",
};

const copyButtonText = {
  en: "Copy CSR",
  am: "Պատճենել CSR",
  ru: "Копировать CSR",
};

export default function SSlGenerateCSR() {
  const [formState, setFormState] = useState({
    common_name: "",
    organization_name: "",
    locality: "",
    state: "",
    country: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [csrValidationLoading, setCsrValidationLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setResult(null);
    setIsSubmitting(true);

    const productCode = localStorage.getItem('ssl_product_code');
    let payload = {};

    if (productCode === 'positivesslwildcard' || productCode === 'sectigowildcard') {
      payload = {
        ...formState, 'common_name': '*.' + formState.common_name,
      };
    } else {
      payload = { ...formState };
    }

    try {
      const response = await axios.post("ssl/csr-generator", payload);
      setResult(response.data);
      localStorage.setItem("ssl_csr", response.data.CSR);
    } catch (submissionError) {
      const message =
        submissionError?.response?.data?.message ||
        submissionError?.message ||
        "Server error";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }

  };

  const scrValidation = async (csr) => {
    setCsrValidationLoading(true);
    const product_code = localStorage.getItem("ssl_product_code");

    try {
      const res = await axios.post("ssl/csr-validation", { csr, product_code });
      localStorage.setItem("ssl_approved_domain_name", res.data.DomainName);
      navigate("/user/ssl/approved-email");
    } catch (validationError) {
      console.log("CSR validation error:", validationError);
      alert("Invalid CSR format. Please check the generated CSR.");
    } finally {
      setCsrValidationLoading(false);
    }
  }

  const renderResult = () => {
    if (!result) return null;

    const csrText =
      result?.data?.csr || result?.csr || result?.csr_pem || result?.csr_pem_text;

    return (
      <div className="csr-result">
        <h2>{pageText.resultTitle['en']}</h2>
        {csrText ? (

          <textarea
            readOnly
            className="csr-output"
            value={csrText}
          />

        ) : (
          <>
            <pre className="csr-output">{result.CSR}</pre>
            <div className="buttons-group">
              <button
                className="btn submit-button"
                onClick={() => scrValidation(result.CSR)}
                disabled={csrValidationLoading}
                style={{ cursor: csrValidationLoading ? "not-allowed" : "pointer" }}
              >
                {csrValidationLoading ? (
                  continiuButtonText['en'].split("/")[1]
                ) : (
                  continiuButtonText['en'].split("/")[0]
                )}
              </button>
              {/* <button className="btn submit-button">
                {copyButtonText['en']}
              </button> */}
            </div>
          </>
        )}

      </div>
    );
  };

  return (
    <div className="SSlGenerateCSRPage wrapper">
      <div className="csr-page-header">
        <h1>{pageText.heading.en}</h1>
      </div>

      <form className="csr-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <label className="form-group">
            <span>Domain Name</span>
            <input
              name="common_name"
              type="text"
              value={formState.common_name}
              onChange={handleChange}
              placeholder="example.com"
              required
            />
          </label>

          <label className="form-group">
            <span>Organization Name</span>
            <input
              name="organization_name"
              type="text"
              value={formState.organization_name}
              onChange={handleChange}
              placeholder="Organization LLC"
              required
            />
          </label>

          <label className="form-group">
            <span>Locality</span>
            <input
              name="locality"
              type="text"
              value={formState.locality}
              onChange={handleChange}
              placeholder="Your City"
              required
            />
          </label>

          <label className="form-group">
            <span>State</span>
            <input
              name="state"
              type="text"
              value={formState.state}
              onChange={handleChange}
              placeholder="Your State"
              required
            />
          </label>

          <label className="form-group">
            <span>Country</span>
            <CountrySelect
              value={formState.country}
              onChange={(code) => setFormState((prev) => ({ ...prev, country: code }))}
            />
          </label>

          <label className="form-group">
            <span>Email</span>
            <input
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              placeholder="example@mail.ru"
              required
            />
          </label>
        </div>

        {error && <div className="form-error">{error}</div>}

        <button type="submit" className="btn submit-button" disabled={isSubmitting}>
          {isSubmitting ? "Loading..." : pageText.submitButton.en}
        </button>
      </form>

      {renderResult()}
    </div>
  );
}
